// ── THE MPCI LEVEL DECISION — one 5-point band at a time ─────────────────────
//
// Kevin, 2026-09-03: SCO + ECO are the frozen default structure for eligible CY2027
// wheat. County trigger frequency, short-window county EV and farm fit survive as
// EVIDENCE and caveats and may no longer switch an endorsement off. This module is the
// engine's whole remaining job — choosing 75, 80 or 85.
//
// ── WHY THE INDIVIDUAL LAYER CAN BE DECIDED WHEN THE AREA LAYER COULD NOT ─────
//
// The area products failed on evidence, and the failure was measured rather than
// argued: a county's settled record is nine years, one of which carried 49% of all SCO
// band payout, and a farm's own history says nothing about whether its COUNTY pays.
// The individual layer is the opposite case — the farm's own yield record is the
// correct and only evidence base for the farm's own shortfalls, and it is what RMA
// rates the policy on. So this test rests on the right data; its weakness is sample
// LENGTH, which is handled explicitly below rather than assumed away.
//
// ── THE TEST: DOES EACH 5-POINT BAND PAY FOR ITSELF? ─────────────────────────
//
// Raising the level does not buy a different product. It buys one more 5-point band of
// the same one, so each band is priced on its own:
//
//     incremental guarantee  = liability(L+5) - liability(L)     $/ac of new protection
//     incremental premium    = net(L+5) - net(L)                 $/ac the producer pays
//     historical value       = what that band would have collected on this farm's record
//
// ⚠ THE MARGINAL SUBSIDY FALLS AS THE LEVEL RISES — RMA's schedule is 59% at 70, 55% at
// 75, 48% at 80, 38% at 85 on basic/optional units — so the 80->85 band costs far more
// than 5/4 of the 75->80 band for the same 5 points of guarantee. Comparing TOTAL
// premium between levels hides that; only the marginal comparison exposes it, which is
// why every figure here is marginal. Measured on the CY2027 book: $8.92/ac against
// $12.00/ac for a near-identical $26/ac of guarantee.
//
// ⚠ AND IT IS PRICED AS REVENUE PROTECTION, NOT YIELD PROTECTION. See bandExperience.
//
// ⚠ AND A SHORT HISTORY MAY NOT CREATE PRECISION. Two separate guards: the test does not
// run at all below MIN_MOVE_YEARS, and above it every band is perturbed by
// leave-one-year-out before it is allowed to move anybody.

export const BAND_POINTS = 0.05;
export const LEVELS = [0.75, 0.80, 0.85];
// Where there is no incumbent to hold and no record to test, this is what stands. It is
// the middle of the offered range, not a recommendation — falling back to the FLOOR
// would assert the cheapest election on no evidence at all.
export const NO_EVIDENCE_LEVEL = 0.80;

// The sample length at which a shortfall frequency has any resolution: at five years
// one bad year is 20% and the estimate cannot separate 10% from 30%. Below it the
// incumbent is held and the record is reported instead.
export const MIN_MOVE_YEARS = 5;
export const MIN_HIGH_CONF_YEARS = 8;

// A band earns its place when its own expected indemnity covers its own marginal
// premium. 1.0 is break-even — not a tuned number, and deliberately not buffered.
export const BAND_BREAKEVEN = 1.0;

// ⚠ THE TOP BAND IS AN EXCEPTION, AND THIS WALK HAD LOST THAT RULE.
//
// Kevin, 2026-09-10: "Rauberdale Stueben County is recommending 85% coverage, we
// basically never recommend 85%, and you know that, because the premium is too
// high. Maybe in a very extreme case we would recommend 85% but not very often."
//
// The rule he is describing is already in this codebase and already documented —
// `MARGIN_UP = 1.25` in coverageRecommend.js, whose own comment reads "85% is the
// exception: 25% clear of break-even". The band walk replaced that path's
// arithmetic and applied ONE bar, plain break-even, to every band. So the
// exception was not overruled; it was dropped, and 85% became an ordinary
// outcome of a test that treats it like any other 5 points.
//
// ⚠ WHY THE TOP BAND SPECIFICALLY, rather than a blanket buffer. RMA's marginal
// subsidy falls as the level rises — 55% at 75, 48% at 80, 38% at 85 — so the
// producer pays for the 80→85 band at close to its gross rate while every band
// below it is bought at a discount. A band at 1.05x break-even is a materially
// different proposition at 38% subsidy than at 55%, because the grower is
// paying nearly all of the load themselves for an estimate this thin.
//
// ⚠ AND IT IS ENFORCED ON THE LEAVE-ONE-OUT MINIMUM, NOT THE HEADLINE RATIO,
// which is the half that does the work. Measured over the CY2026/27 book on
// 2026-09-10 — 125 units with a reported acreage, 22 priceable 80→85 bands —
// the seven farms the walk sent to 85% carried full-sample ratios of 1.28 to
// 1.49, so a 1.25x test on the ratio alone would have moved NOBODY. Their
// leave-one-out minima are 1.02, 1.08, 1.09, 1.11, 1.16, 1.34 and 1.38: the
// band's value on a 7-to-10 year record is carried by one or two deep-loss
// years, and looMin is exactly the resample with the single most valuable year
// removed. Requiring the margin to survive that is the robustness test; the
// headline ratio is not.
//
// Measured effect, stated rather than absorbed: 85% goes from 7 of 125 priceable
// units to 2 — WOODLAWN FARMS (CV 33%, looMin 1.38) and KAST FARMS (CV 28%,
// looMin 1.34), the two most volatile wheat farms in the book. That is the
// "very extreme case" shape. RAUBERDALE (CV 18%, looMin 1.11) returns to its
// own 80% election.
//
// ⚠ THIS NUMBER IS A STATED AGENCY JUDGMENT, NOT A MEASUREMENT, exactly as
// MARGIN_UP always was. It is Kevin's to move, and every surface prints the
// band's own ratio beside it so a 1.20 is visible rather than hidden behind a
// verdict.
export const BAND_MARGIN_TOP = 1.25;

// The bar a given band has to clear. Break-even everywhere except the top band.
export function barForBand(to) {
  return Math.abs(to - LEVELS[LEVELS.length - 1]) < 1e-9 ? BAND_MARGIN_TOP : BAND_BREAKEVEN;
}

export const EVID = {
  SUPPORTS: "CLEARLY_SUPPORTS",
  BORDERLINE: "BORDERLINE",
  NOT: "CLEARLY_DOES_NOT_SUPPORT",
  UNTESTABLE: "UNTESTABLE",
};
export const EVID_LABEL = {
  [EVID.SUPPORTS]: "clearly supported",
  [EVID.BORDERLINE]: "too close to call",
  [EVID.NOT]: "not supported",
  [EVID.UNTESTABLE]: "not testable",
};

const mean = (a) => (a.length ? a.reduce((x, y) => x + y, 0) / a.length : null);

// ── the farm's own downside record, normalised to its approved yield ─────────
//
// Expressed as a RATIO to APH so the numbers line up with the coverage levels they are
// being compared against, and so units of different size are comparable.
export function yieldProfile(series, aph) {
  const ok = (series || []).filter((s) => s && +s.y > 0 && s.yr != null);
  if (!(aph > 0) || !ok.length) return { n: 0, usable: false, years: [], ratios: [] };
  const ratios = ok.map((s) => +s.y / aph);
  const n = ratios.length;
  const m = mean(ratios);
  const sd = n > 1 ? Math.sqrt(ratios.reduce((a, r) => a + (r - m) * (r - m), 0) / (n - 1)) : null;
  const below = (L) => ratios.filter((r) => r < L).length;
  // Severity is measured only over the years that actually fell short. Averaging the
  // shortfall across ALL years dilutes a real loss with good years and reads low.
  const sev = (L) => {
    const s = ratios.filter((r) => r < L).map((r) => L - r);
    return s.length ? mean(s) / L : null;
  };
  return {
    n, usable: true, years: ok.map((s) => +s.yr), ratios,
    meanRatio: m, cv: sd != null && m > 0 ? (100 * sd) / m : null,
    worst: Math.min(...ratios), best: Math.max(...ratios),
    freq85: below(0.85) / n, freq80: below(0.80) / n, freq75: below(0.75) / n,
    nBelow85: below(0.85), nBelow80: below(0.80), nBelow75: below(0.75),
    sev85: sev(0.85), sev80: sev(0.80), sev75: sev(0.75),
  };
}

// ── the harvest/projected price ratio per crop year, from RMA's own numbers ──
//
// ⚠ THE ROWS MUST CARRY projected_price AND harvest_price. They are a national pair per
// crop year — verified identical across every county in the table — so any county's
// rows answer for all of them. A caller whose query omits those columns gets h = 1 on
// every year, which silently reverts this module to a yield-only test; `unpricedYears`
// on the result is what makes that visible instead of quiet.
export function priceRatiosFrom(areaRows) {
  const out = {};
  for (const r of areaRows || []) {
    const p = r && r.projected_price != null ? +r.projected_price : null;
    const h = r && r.harvest_price != null ? +r.harvest_price : null;
    const yr = r && r.crop_year != null ? +r.crop_year : null;
    if (yr == null || !(p > 0) || h == null) continue;
    out[yr] = h / p;
  }
  return out;
}

// ── what one 5-point band would have paid on this farm's own record ──────────
//
// ⚠ THIS IS REVENUE PROTECTION AND THE DIFFERENCE FROM YIELD PROTECTION IS LARGE.
// A yield-only test asks whether the farm's yield fell into the band. RP also pays when
// the harvest price falls, and the harvest-price provision makes that asymmetric:
//
//     guarantee  G(L) = L x max(1, h)        h = harvest price / projected price
//     revenue    A    = y x h                y = yield / APH
//     indemnity  I(L) = max(0, G(L) - A)     normalised by APH x projected price
//
// When h >= 1 the guarantee rises with the price and the trigger stays at y < L. When
// h < 1 the guarantee holds at L while revenue falls, so the effective yield trigger
// rises to y < L/h. On RMA's own wheat prices h was 0.808 in 2023 — at an 80% election
// that year ANY yield below 99% of APH triggered.
//
// ⚠ AND THE FILL IS A DIFFERENCE OF TWO INDEMNITIES, NOT A BETWEEN-THE-POINTS TEST.
// Once revenue falls below the lower guarantee the whole incremental band is exhausted,
// so a deep loss credits the full 5 points. Asking instead whether the result "landed
// between the two attachment points" credits ZERO on a total loss — verified against RP
// recomputed from full dollars in tools/check-wheat-bands.js.
export function bandExperience(profile, from, aph, price, priceRatios) {
  if (!profile || !profile.usable || !(aph > 0) || !(price > 0)) return null;
  const to = from + BAND_POINTS;
  const ind = (L, y, h) => Math.max(0, L * Math.max(1, h) - y * h);
  let unpricedYears = 0;
  const fills = profile.ratios.map((y, i) => {
    const h = priceRatios && priceRatios[profile.years[i]] > 0 ? priceRatios[profile.years[i]] : null;
    if (h == null) unpricedYears++;
    const hh = h == null ? 1 : h;
    return ind(to, y, hh) - ind(from, y, hh);
  });
  const meanFill = mean(fills);
  return {
    from, to, nYears: profile.n, unpricedYears,
    yearsPaying: fills.filter((f) => f > 1e-9).length,
    expIndemnity: meanFill * aph * price,
    maxIndemnity: BAND_POINTS * aph * price,
    worstYearFill: Math.max(...fills),
  };
}

// ── how strong is the evidence for ONE band? ─────────────────────────────────
//
// Leave-one-year-out over the farm's own record — the sample the band test rests on.
// A band is CLEARLY decided only when every leave-one-out sample agrees with the full
// sample. BORDERLINE is not a soft yes or a soft no; it is the statement that a
// nine-year record cannot separate $14.86 of value from $14.92 of cost.
export function classifyBand(profile, from, margPrem, aph, price, priceRatios) {
  if (!(margPrem > 0) || !profile || !profile.usable || profile.n < 2)
    return { evidence: EVID.UNTESTABLE, looMin: null, looMax: null };
  const loo = [];
  for (let i = 0; i < profile.n; i++) {
    const sub = {
      usable: true, n: profile.n - 1,
      years: profile.years.filter((_, j) => j !== i),
      ratios: profile.ratios.filter((_, j) => j !== i),
    };
    loo.push(bandExperience(sub, from, aph, price, priceRatios).expIndemnity / margPrem);
  }
  const allAbove = loo.every((r) => r >= BAND_BREAKEVEN);
  const allBelow = loo.every((r) => r < BAND_BREAKEVEN);
  return {
    evidence: allAbove ? EVID.SUPPORTS : allBelow ? EVID.NOT : EVID.BORDERLINE,
    looMin: Math.min(...loo), looMax: Math.max(...loo), looN: loo.length,
  };
}

// ── the decision ─────────────────────────────────────────────────────────────
//
// Walk UP from 75, one band at a time, owning a band according to its evidence:
//
//     CLEARLY_SUPPORTS  -> own it
//     CLEARLY_DOES_NOT  -> do not own it, so an incumbent may come DOWN through it
//     BORDERLINE        -> own it if and only if the incumbent already does
//
// ⚠ BORDERLINE DEFERS TO THE INCUMBENT RATHER THAN TO A DEFAULT, and that asymmetry is
// the point. Where the record cannot decide, the grower's existing election stands and
// nobody is moved on evidence that was never gathered. It is not a buffer — the
// break-even arithmetic is untouched — it is a rule about who wins when evidence is
// silent.
//
// Walking up also means the 80->85 band is never owned on the strength of the 75->80
// band's economics. Each is separately priced and separately earned.
//
// ⚠ THE MARGINAL COST IS NET OF THE SCO PREMIUM THE BAND DISPLACES, and the caller
// supplies it rather than this module recomputing one. Raising the MPCI level shrinks
// the SCO band above it, so the producer stops paying for the points they just bought
// individually — charging only the MPCI increase overstates every buy-up. That netting
// rule already exists as `marginalMove` in coverageStack.js and is guarded there; a
// second copy here is how the two would come to disagree about one farm.
//
// `marginal(from, to)` -> { netCostPerAc, liabilityMovedPerAc } or null.
// `ladder` is the set of levels to walk, lowest first, adjacent by one 5-point band.
// It defaults to the historical 75/80/85 so every existing caller is unchanged.
//
// ⚠ THE BAR FOR THE TOP BAND STAYS KEYED ON RMA'S OWN TOP (0.85), NOT ON THE TOP OF
// THE WALKED SET. `barFor` reads LEVELS[LEVELS.length - 1] deliberately: the extra
// margin exists because 85% is the most expensive coverage RMA sells, and a farm at
// 60% whose ladder tops out at 65% must not have 65% held to the 85% bar.
export function chooseBandLevel({ profile, marginal, aph, price, incumbent, priceRatios, ppMaterial = false, ladder = LEVELS }) {
  const bands = [];
  const trace = [];
  const RUNGS = Array.isArray(ladder) && ladder.length >= 2 ? ladder.slice().sort((a, b) => a - b) : LEVELS;

  // ⚠ AN INCUMBENT BELOW THE WALKED LADDER SITS UNDER A BAND THIS ENGINE NEVER
  // EVALUATES, and promoting such a grower to the floor would be exactly the
  // unevidenced upward move the BORDERLINE rule prevents one band higher.
  //
  // This used to fire for EVERY grower under 75%, because the ladder was the fixed
  // 75/80/85 — so the 266 of 336 base RP wheat coverages below 80% were told their election
  // "is below the range this comparison covers" and nothing was ever measured for
  // them. With the ladder now built around the incumbent, a renewal is never below
  // its own ladder and this branch is reached only by a level RMA does not sell.
  if (incumbent != null && incumbent < RUNGS[0] - 1e-9) {
    trace.push({ rule: "INCUMBENT_BELOW_RANGE", detail:
      "the current " + Math.round(incumbent * 100) + "% election sits below the "
      + Math.round(RUNGS[0] * 100) + "% floor of the quoted range, and the band beneath that floor is not evaluated" });
    return { level: incumbent, held: true, belowRange: true, bands, trace,
      why: "the current " + Math.round(incumbent * 100) + "% election is below the "
        + Math.round(RUNGS[0] * 100) + "–" + Math.round(RUNGS[RUNGS.length - 1] * 100) + "% range this "
        + "comparison covers, so it is held rather than moved on evidence that was not gathered" };
  }

  if (!profile || !profile.usable || profile.n < MIN_MOVE_YEARS) {
    const n = profile ? profile.n : 0;
    trace.push({ rule: "HISTORY_TOO_SHORT", detail: n + " usable yield years, floor is " + MIN_MOVE_YEARS });
    return { level: incumbent != null ? incumbent : NO_EVIDENCE_LEVEL, held: true, shortHistory: true, bands, trace,
      why: n
        ? n + " year" + (n === 1 ? "" : "s") + " of yield history is too short to show how often this farm "
          + "falls into a 5-point band, so " + (incumbent != null ? "the current election is held"
            : "the 80% benchmark stands")
        : "no usable wheat yield history for this county and practice, so "
          + (incumbent != null ? "the current election is held" : "the 80% benchmark stands") };
  }

  let level = RUNGS[0];
  for (let i = 0; i < RUNGS.length - 1; i++) {
    const from = RUNGS[i], to = RUNGS[i + 1];
    const mv = marginal ? marginal(from, to) : null;
    const exp = bandExperience(profile, from, aph, price, priceRatios);
    // A band that costs nothing net — the SCO saving covers the MPCI increase — is
    // owned outright. There is no break-even to clear when the price is zero.
    //
    // ⚠ NULL IS NOT ZERO, AND `null <= 0` IS TRUE. A band whose premium could not
    // be computed (no reported acreage, so scrubPremium nulls the stack) walked
    // into this branch and came out `free: true, own: true` with margPrem null —
    // the grower told they own a band outright on the strength of a number nobody
    // could calculate. It printed as "$0.00/ac of premium" on the Full Strategy
    // Report. An unpriced band belongs in NOT_PRICEABLE below, which already
    // exists and already says so.
    if (mv && exp && mv.netCostPerAc != null && mv.netCostPerAc <= 0) {
      bands.push({ from, to, priceable: true, free: true, own: true,
        incrGuarantee: mv.liabilityMovedPerAc, margPrem: mv.netCostPerAc,
        expIndemnity: exp.expIndemnity, ratio: null, evidence: EVID.SUPPORTS,
        looMin: null, looMax: null, incOwns: incumbent != null && incumbent >= to - 1e-9,
        yearsPaying: exp.yearsPaying, nYears: exp.nYears, unpricedYears: exp.unpricedYears });
      trace.push({ rule: "BAND_FREE", band: bandName(from, to), own: true,
        detail: "net of the SCO premium it displaces this band costs nothing" });
      level = to;
      continue;
    }
    if (!mv || !exp || !(mv.netCostPerAc > 0)) {
      bands.push({ from, to, priceable: false });
      trace.push({ rule: "BAND_NOT_PRICEABLE", band: bandName(from, to) });
      break;
    }
    const margPrem = mv.netCostPerAc;
    const ratio = exp.expIndemnity / margPrem;
    const cls = classifyBand(profile, from, margPrem, aph, price, priceRatios);
    // Does the grower already own this band? A band is owned when the incumbent
    // election reaches its top — that is what BORDERLINE defers to.
    const incOwns = incumbent != null && incumbent >= to - 1e-9;
    // ⚠ THE MARGIN IS A SEPARATE GATE FROM THE EVIDENCE, ON PURPOSE. `evidence`
    // stays a statement about BREAK-EVEN — whether the band covered its own cost
    // and whether every resample agrees — because that is what the sentences on
    // screen say, and folding a 1.25x bar into it would make a band at 1.10x
    // print as "has not covered its cost", which is false. The band DID cover
    // its cost; it did not clear the bar the top level is held to. Two different
    // facts, and the card states both.
    const bar = barForBand(to);
    const marginShort = bar > BAND_BREAKEVEN
      && cls.evidence === EVID.SUPPORTS
      && !(ratio >= bar && cls.looMin != null && cls.looMin >= bar);
    const own = marginShort ? incOwns
      : cls.evidence === EVID.SUPPORTS ? true
      : cls.evidence === EVID.NOT ? false
      : incOwns;
    bands.push({ from, to, priceable: true,
      incrGuarantee: mv.liabilityMovedPerAc, margPrem,
      expIndemnity: exp.expIndemnity, ratio,
      yearsPaying: exp.yearsPaying, nYears: exp.nYears, unpricedYears: exp.unpricedYears,
      evidence: cls.evidence, looMin: cls.looMin, looMax: cls.looMax, incOwns, own,
      bar, marginShort });

    const econ = "$" + exp.expIndemnity.toFixed(2) + "/ac of historical value against $"
      + margPrem.toFixed(2) + "/ac of premium (" + exp.yearsPaying + " of " + exp.nYears
      + " years, " + ratio.toFixed(2) + "x)";
    const loo = cls.looMin == null ? "" : " (" + cls.looMin.toFixed(2) + "–" + cls.looMax.toFixed(2) + "x)";
    if (marginShort)
      trace.push({ rule: "BAND_BELOW_TOP_MARGIN", band: bandName(from, to), own: incOwns,
        detail: econ + " — it covers its own cost, but the top level is held to "
          + BAND_MARGIN_TOP + "x and this does not clear that once the single best year is "
          + "dropped" + loo + ", so "
          + (incOwns ? "the current election stands there" : "it is not added") });
    else if (cls.evidence === EVID.SUPPORTS)
      trace.push({ rule: "BAND_SUPPORTED", band: bandName(from, to), own: true,
        detail: econ + "; every leave-one-year-out sample agrees" + loo });
    else if (cls.evidence === EVID.NOT)
      trace.push({ rule: "BAND_NOT_SUPPORTED", band: bandName(from, to), own: false,
        detail: econ + "; every leave-one-year-out sample agrees" + loo });
    else
      trace.push({ rule: "BAND_BORDERLINE_HOLD", band: bandName(from, to), own: incOwns,
        detail: econ + "; the decision reverses across leave-one-year-out" + loo
          + ", so the current election stands — "
          + (incOwns ? "this band is already carried, so it is kept"
                     : "this band is not currently carried, so it is not added") });

    // ⚠ PREVENTED PLANTING IS A FLOOR ON THE WAY DOWN, NEVER A REASON TO BUY UP.
    // The PP guarantee is the PP factor applied to THIS level, so lowering the level
    // lowers what a prevented-planting year pays — an exposure the yield-band test
    // cannot see, because a PP year enters the APH database at its PP yield rather than
    // as the shortfall it really was. So a farm with a PP pattern does not drop through
    // a band it already carries on band economics alone. It pushes nobody UP: PP is a
    // reason to keep a guarantee, never to buy a bigger one.
    if (!own && ppMaterial && incOwns) {
      trace.push({ rule: "PP_HOLDS_BAND", band: bandName(from, to), own: true,
        detail: "the band does not clear its own cost, but this farm has a prevented-planting "
          + "pattern on record and the PP guarantee is applied to this level, so it is kept" });
      bands[bands.length - 1].own = true;
      bands[bands.length - 1].ppHeld = true;
      level = to;
      continue;
    }
    if (!own) break;
    level = to;
  }

  const ppHeld = bands.some((b) => b.ppHeld);
  const stopper = bands.find((b) => b.priceable && !b.own);
  const priced = bands.filter((b) => b.priceable);
  const why = !stopper && priced.length
    ? "every buy-up band is clearly supported by this farm's own yield record"
    : stopper
      // ⚠ THE MARGIN CASE MUST NOT BORROW THE "did not cover its cost" SENTENCE.
      // It did cover its cost. Saying otherwise on a card that also prints the
      // ratio is the self-contradiction this whole pass exists to remove.
      ? (stopper.marginShort
          ? "the " + bandName(stopper.from, stopper.to) + " band covers its own cost on this "
            + "farm's record ($" + stopper.expIndemnity.toFixed(2) + " of value against $"
            + stopper.margPrem.toFixed(2) + " of premium, " + stopper.ratio.toFixed(2) + "x) but "
            + "not by the margin the top level is held to (" + BAND_MARGIN_TOP + "x), and it falls to "
            + stopper.looMin.toFixed(2) + "x if a single year is dropped — the subsidy is lowest on "
            + "these five points, so they are bought only on a clear margin"
          : stopper.evidence === EVID.BORDERLINE
          ? "the " + bandName(stopper.from, stopper.to) + " band is too close to call on "
            + stopper.nYears + " years ($" + stopper.expIndemnity.toFixed(2) + " of value against $"
            + stopper.margPrem.toFixed(2) + " of premium, reversing across leave-one-out), so the "
            + "current election stands there"
          : "this farm's own record does not fall into the " + bandName(stopper.from, stopper.to)
            + " band often enough to cover its cost — " + stopper.yearsPaying + " of " + stopper.nYears
            + " years, worth $" + stopper.expIndemnity.toFixed(2) + "/ac against $"
            + stopper.margPrem.toFixed(2) + "/ac of premium")
      : "no priceable buy-up band";

  return { level, held: false, bands, trace, ppHeld,
    why: ppHeld ? "a prevented-planting pattern on this farm's record holds the current level — "
      + "lowering it would lower the PP guarantee with it" : why,
    decidedBy: ppHeld ? "PP_FLOOR"
      : stopper ? (stopper.marginShort ? "TOP_BAND_MARGIN" : stopper.evidence)
      : "ALL_SUPPORTED",
    // A level reached only because BORDERLINE deferred to the incumbent is a HOLD in
    // substance even when the number changes nothing — callers that report "why" need
    // to be able to tell that from an affirmatively evidenced pick.
    restsOnIncumbent: bands.some((b) => b.priceable && b.evidence === EVID.BORDERLINE && b.own === b.incOwns) };
}

function bandName(a, b) { return Math.round(a * 100) + "→" + Math.round(b * 100) + "%"; }

// ── confidence, reported separately from the recommendation, always ──────────
export function bandConfidence(profile, bands) {
  if (!profile || !profile.usable || !profile.n)
    return { level: "INSUFFICIENT", why: "no usable yield history" };
  let level = profile.n >= MIN_HIGH_CONF_YEARS ? "HIGH"
    : profile.n >= MIN_MOVE_YEARS ? "MODERATE" : "LOW";
  let why = profile.n + " usable yield years";
  // A band whose decision reverses when one year is dropped is not a high-confidence
  // band, whatever the record length says.
  if ((bands || []).some((b) => b.priceable && b.evidence === EVID.BORDERLINE)) {
    level = level === "HIGH" ? "MODERATE" : level === "MODERATE" ? "LOW" : level;
    why += " — held down one band because a buy-up decision reverses if a single year is dropped";
  }
  return { level, why };
}
